import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import { getMe } from "@/lib/api";

const EMAILJS_SERVICE_ID = "service_1tfrkxp";
const EMAILJS_TEMPLATE_ID = "template_3zf5nnr";
const EMAILJS_PUBLIC_KEY = "7X5_mZTv4rENUum25";

export default function SupportButton() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "sugerencia", message: "" });
  const [userEmail, setUserEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const handleOpen = async () => {
    setOpen((o) => !o);
    if (!userEmail) {
      try {
        const me = await getMe();
        if (me?.email) setUserEmail(me.email);
      } catch (_e) {}
    }
  };

  const handleSend = async () => {
    if (!form.message.trim()) return;
    setSending(true);
    setError(false);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        { type: form.type, message: form.message, from_email: userEmail || "anónimo" },
        EMAILJS_PUBLIC_KEY
      );
      setSent(true);
      setTimeout(() => {
        setSent(false);
        setForm({ type: "sugerencia", message: "" });
        setOpen(false);
      }, 2500);
    } catch (_e) {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const TYPES = [
    { id: "sugerencia", label: "💡 Sugerencia" },
    { id: "bug", label: "🐛 Bug / error" },
    { id: "soporte", label: "🙋 Soporte" },
  ];

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-20 right-4 z-50 w-80 bg-[#FFFDF5] border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
          >
            <div className="bg-[#101010] px-4 py-3 flex items-center justify-between">
              <div>
                <div className="font-display uppercase text-white text-base leading-tight">Contacto</div>
                <div className="font-mono-cm text-[10px] uppercase tracking-widest text-white/50">
                  Sugerencias · Bugs · Soporte
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white font-display text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {sent ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-6 text-center"
              >
                <div className="text-4xl mb-3">✅</div>
                <div className="font-display uppercase text-lg">¡Gracias!</div>
                <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 mt-1">
                  Tu mensaje fue enviado
                </div>
              </motion.div>
            ) : (
              <div className="p-4 space-y-3">
                <div className="flex gap-2">
                  {TYPES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setForm((f) => ({ ...f, type: t.id }))}
                      className={`cm-press flex-1 border-2 border-[#101010] rounded-lg px-2 py-1.5 font-mono-cm text-[9px] uppercase tracking-widest transition-colors ${
                        form.type === t.id ? "bg-[#FFDE59]" : "bg-white"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <textarea
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  placeholder={
                    form.type === "bug"
                      ? "Describí qué pasó y en qué pantalla..."
                      : form.type === "soporte"
                      ? "¿En qué te puedo ayudar?"
                      : "¿Qué mejorarías del juego?"
                  }
                  maxLength={500}
                  rows={4}
                  className="w-full border-4 border-[#101010] rounded-xl px-3 py-2.5 text-sm font-medium resize-none focus:outline-none bg-white"
                />

                {error && (
                  <div className="font-mono-cm text-[9px] uppercase tracking-widest text-[#FF4C4C] text-center">
                    Error al enviar. Intentá de nuevo.
                  </div>
                )}

                <button
                  onClick={handleSend}
                  disabled={!form.message.trim() || sending}
                  className="cm-press w-full bg-[#FF4C4C] text-white border-4 border-[#101010] rounded-xl py-3 font-display uppercase text-base cm-shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sending ? "Enviando..." : "Enviar →"}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={handleOpen}
        whileTap={{ scale: 0.92 }}
        className="fixed bottom-4 right-4 z-50 w-12 h-12 bg-[#101010] text-white border-4 border-[#101010] rounded-full cm-shadow flex items-center justify-center text-xl"
        title="Contacto y soporte"
        data-testid="support-button"
      >
        {open ? "✕" : "💬"}
      </motion.button>
    </>
  );
}
